/**
 * 🚀 PIXIE GLOBAL - Personnage Pixie (avatar global)
 * Pixie = Belle blonde classe, commandante aux commandes de sa cabine
 * - Téléportation multivers, parle toutes les langues
 * - Cultivée, taquine, inaccessible mais ensorcelante
 * - La "rabateuse" ultime vers Heartbeats !
 * 
 * Stratégie : Pixie apparaît partout pour créer l'envie
 * avec des messages charmeurs adaptés au contexte !
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, X, Sparkles, ChevronRight, ShoppingCart, Users, MapPin, Truck, Gift, Clock } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

// 🎨 Image de Pixie - Belle blonde classe (commandante)
const PIXIE_IMAGE = 'https://customer-assets.emergentagent.com/job_heartbeats-revamp/artifacts/jybu2wd3_88835866-396a-4af5-8f1b-1ff67b42abbe.png';

// Garder les anciennes pour compatibilité mais pointer vers la nouvelle
const PIXIE_IMAGES = {
  magical_smile: PIXIE_IMAGE,
  matchmaker_wink: PIXIE_IMAGE,
  mischievous_pout: PIXIE_IMAGE,
  dreamy_romantic: PIXIE_IMAGE,
  flirty_tease: PIXIE_IMAGE,
  default: PIXIE_IMAGE,
};

// 💬 Messages de Pixie par CONTEXTE DE PAGE
// 🎯 OBJECTIF : Inciter les gens à VENIR AU MAGASIN avec curiosité !
const PIXIE_MESSAGES_BY_CONTEXT = {
  // 🏠 Page d'accueil / Landing
  landing: [
    {
      message: "Psst... Tu veux connaître LE secret dont tous nos clients parlent ? 🤫 Passe au magasin ! 7j/7 de Midi à Minuit, 🌙 Ramadan: Ven-Sam jusqu'à 4h !",
      expression: 'matchmaker_wink',
      cta: "Découvrir le secret 🔮",
    },
    {
      message: "Hey ! 👋 Nos clients gardent un secret jalousement... Tu veux savoir ce que c'est ? Viens au magasin ! 😏",
      expression: 'magical_smile',
      cta: "Je veux savoir ! ✨",
    },
    {
      message: "Tu savais qu'il se passe des trucs INCROYABLES dans notre épicerie ? 🤩 Passe nous voir ! 7j/7 Midi-Minuit, 🌙 Ramadan: Ven-Sam jusqu'à 4h !",
      expression: 'mischievous_pout',
      cta: "C'est quoi ? 🔥",
    },
    {
      message: "Je suis Pixie, la gardienne d'un secret que seuls les clients connaissent ! 🧚‍♀️ Tu veux être dans la confidence ?",
      expression: 'dreamy_romantic',
      cta: "Dis-moi tout ! 💜",
    },
    {
      message: "Nos clients sont accros à un truc... et c'est pas que les bons produits ! 😏 Viens découvrir par toi-même !",
      expression: 'matchmaker_wink',
      cta: "Je passe au magasin 🛒",
    },
  ],
  
  // 🚚 Page Livraison
  livraison: [
    {
      message: "La livraison c'est pratique... mais tu rates quelque chose de DINGUE au magasin ! 🤫 Viens voir !",
      expression: 'matchmaker_wink',
      cta: "C'est quoi ? 💕",
    },
    {
      message: "Livraison gratuite 🌙 Ramadan: Ven-Sam jusqu'à 4h du mat' ! 🚚 Mais le vrai cadeau, c'est ce qui t'attend en magasin... 😏",
      expression: 'mischievous_pout',
      cta: "Dis-moi ! 😂",
    },
    {
      message: "Tu te fais livrer ? Cool ! Mais tu devrais quand même passer au magasin... Y'a un secret que tu dois découvrir ! 🤩",
      expression: 'dreamy_romantic',
      cta: "J'arrive ! 💕",
    },
  ],
  
  // 🎁 Page Offre Bienvenue
  bienvenue: [
    {
      message: "-10% c'est bien... mais le vrai cadeau c'est ce que tu vas découvrir EN MAGASIN ! 🎁 Viens !",
      expression: 'magical_smile',
      cta: "Je veux savoir ! 💕",
    },
    {
      message: "Bienvenue ! 🎉 Tu sais que nos clients ont accès à quelque chose d'EXCLUSIF ? Passe au magasin pour découvrir !",
      expression: 'matchmaker_wink',
      cta: "J'arrive ! ✨",
    },
    {
      message: "Nouveau ici ? 🆕 Parfait timing ! Il se passe des trucs de ouf au magasin... Viens voir ! 😏",
      expression: 'mischievous_pout',
      cta: "Dis-moi plus ! 🔥",
    },
  ],
  
  // 📍 Page Ville / Local
  local: [
    {
      message: "Tu cherches une épicerie ? 🏪 Tu vas trouver BIEN PLUS que des courses chez nous ! Viens voir le secret !",
      expression: 'matchmaker_wink',
      cta: "Je passe ! 💕",
    },
    {
      message: "Épicerie ouverte 7j/7 Midi-Minuit, Ven-Sam jusqu'à 2h ! 🌙 Et il s'y passe des choses que tu dois absolument découvrir...",
      expression: 'magical_smile',
      cta: "C'est quoi ? ✨",
    },
    {
      message: "Dans ce quartier, il y a un secret que tout le monde se chuchote... 🤫 Passe au magasin, tu comprendras !",
      expression: 'mischievous_pout',
      cta: "Je veux savoir ! 😂",
    },
  ],
  
  // 🔍 Page Recherche
  recherche: [
    {
      message: "Tu cherches quelque chose ? 🔍 Moi j'ai trouvé un truc INCROYABLE au magasin... Tu veux savoir ce que c'est ?",
      expression: 'matchmaker_wink',
      cta: "Dis-moi ! 💕",
    },
    {
      message: "Recherche 'secret du magasin' dans Google... Ah non, ça marche pas ! 😂 Viens le découvrir toi-même !",
      expression: 'mischievous_pout',
      cta: "Se connecter 🔥",
    },
  ],
  
  // 📜 Page Historique
  historique: [
    {
      message: "Tu regardes ton historique ? 📜 Passe au magasin, y'a toujours des surprises qui t'attendent ! 🤩",
      expression: 'dreamy_romantic',
      cta: "Je passe ! 💕",
    },
    {
      message: "Tes commandes c'est bien... mais tu rates le meilleur ! 😏 Viens découvrir le secret au magasin !",
      expression: 'matchmaker_wink',
      cta: "C'est quoi ? ✨",
    },
  ],
  
  // 💼 Page Pro / B2B
  pro: [
    {
      message: "Tu es commerçant ? 🏪 Contacte-nous pour installer cette magie dans TON magasin ! 😏",
      expression: 'matchmaker_wink',
      cta: "En savoir plus 💼",
    },
    {
      message: "Fidéliser tes clients c'est bien... Les faire revenir encore et encore c'est GÉNIAL ! 🧚‍♀️💕",
      expression: 'magical_smile',
      cta: "Nous contacter 🚀",
    },
  ],
  
  // 📧 Page Email / Confirmation
  email: [
    {
      message: "Email confirmé ? ✅ Super ! Maintenant viens au magasin découvrir le secret ! 🤫",
      expression: 'matchmaker_wink',
      cta: "J'arrive ! ✨",
    },
  ],
  
  // 🚫 Page Désinscription
  desinscription: [
    {
      message: "Tu nous quittes ? 😢 Mais... tu n'as même pas découvert LE secret du magasin !",
      expression: 'dreamy_romantic',
      cta: "Dis-moi ! 💜",
    },
    {
      message: "Attends ! 🧚‍♀️ Avant de partir, passe au magasin ! Tu vas ADORER ce qui t'attend...",
      expression: 'magical_smile',
      cta: "C'est quoi ? ✨",
    },
  ],
  
  // 🔐 Espace Client (pour ceux qui sont déjà connectés)
  client: [
    {
      message: "Hey ! 👋 Tu connais le secret dont tout le monde parle ? Passe au magasin ! Ouvert jusqu'à MINUIT ! 🌙",
      expression: 'matchmaker_wink',
      cta: "J'y vais ! 💕",
    },
    {
      message: "Points fidélité c'est cool... mais le vrai trésor c'est ce qui t'attend EN MAGASIN ! 😏 Viens voir !",
      expression: 'mischievous_pout',
      cta: "C'est quoi ? ✨",
    },
  ],
  
  // 🎯 Default / Autres pages
  default: [
    {
      message: "Psst... Tu veux connaître LE secret dont tout le monde parle ? 🤫 Passe au magasin !",
      expression: 'matchmaker_wink',
      cta: "Je veux savoir ! 💕",
    },
    {
      message: "Je suis Pixie ! 🧚‍♀️ Je garde un secret... Viens au magasin, on est ouvert jusqu'à MINUIT !",
      expression: 'magical_smile',
      cta: "J'arrive ! ✨",
    },
    {
      message: "Nos clients ont un secret jalousement gardé... 😏 Tu veux être dans la confidence ? Passe au magasin !",
      expression: 'dreamy_romantic',
      cta: "Dis-moi plus ! 🔥",
    },
  ],
};

// 🎯 Mapping URL -> Contexte
const getContextFromPath = (pathname) => {
  if (pathname === '/' || pathname === '/landing') return 'landing';
  if (pathname.includes('livraison')) return 'livraison';
  if (pathname.includes('bienvenue') || pathname.includes('offre')) return 'bienvenue';
  if (pathname.includes('local') || pathname.includes('city') || pathname.includes('ville')) return 'local';
  if (pathname.includes('search') || pathname.includes('recherche')) return 'recherche';
  if (pathname.includes('history') || pathname.includes('historique')) return 'historique';
  if (pathname.includes('pro') || pathname.includes('b2b') || pathname.includes('business')) return 'pro';
  // La route /heartbeats redirige vers /client - pas de contexte spécifique
  if (pathname.includes('email') || pathname.includes('confirm')) return 'email';
  if (pathname.includes('desinscri') || pathname.includes('unsubscribe')) return 'desinscription';
  if (pathname.includes('client') || pathname.includes('dashboard') || pathname.includes('portal')) return 'client';
  return 'default';
};

/**
 * 🧚‍♀️ PixieGlobal - Composant universel
 */
export function PixieGlobal({ 
  delay = 6000,
  position = 'bottom-right',
  forceContext = null, // Pour forcer un contexte spécifique
}) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [currentMessage, setCurrentMessage] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // 🚨 DÉSACTIVER COMPLÈTEMENT SUR HEARTBEATS.FR
  const hostname = window.location.hostname.toLowerCase();
  const isHeartbeatsDomain = hostname.includes('heartbeats');
  
  // 🎯 Déterminer le contexte basé sur l'URL
  const context = forceContext || getContextFromPath(location.pathname);
  
  // ⏰ Timer d'apparition
  useEffect(() => {
    // 🚫🚫🚫 JAMAIS SUR HEARTBEATS.FR 🚫🚫🚫
    if (isHeartbeatsDomain) {
      return;
    }
    
    // 🚫 Ne pas afficher sur les pages admin
    if (location.pathname.includes('admin') || 
        location.pathname.includes('cockpit') || 
        location.pathname.includes('driver')) {
      return;
    }
    
    // 🚫 Ne pas afficher sur l'espace client LPE (déjà connecté)
    if (location.pathname === '/client' ||
        location.pathname.startsWith('/client')) {
      return;
    }
    
    // 🚫 NE PAS AFFICHER SUR LES PAGES HEARTBEATS !
    if (location.pathname.includes('heartbeats') ||
        location.pathname.includes('/auth') ||
        location.pathname.includes('/pricing') ||
        location.pathname.includes('/heartbeats-preview') ||
        location.pathname.includes('/heartbeats-portal')) {
      return;
    }
    
    // Vérifier si déjà vu récemment sur cette page
    const storageKey = `pixie_seen_${context}`;
    const lastSeen = localStorage.getItem(storageKey);
    const now = Date.now();
    
    // 🛑 Cooldown AUGMENTÉ à 4 heures par contexte (au lieu de 30 min)
    if (lastSeen && now - parseInt(lastSeen) < 14400000) {
      return;
    }
    
    const timer = setTimeout(() => {
      const messages = PIXIE_MESSAGES_BY_CONTEXT[context] || PIXIE_MESSAGES_BY_CONTEXT.default;
      const randomIndex = Math.floor(Math.random() * messages.length);
      setCurrentMessage(messages[randomIndex]);
      setIsVisible(true);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [delay, context, location.pathname]);
  
  // Reset quand on change de page
  useEffect(() => {
    setIsVisible(false);
    setIsDismissed(false);
  }, [location.pathname]);
  
  // 🚪 Fermer
  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    localStorage.setItem(`pixie_seen_${context}`, Date.now().toString());
  };
  
  // 💜 Aller vers l'espace CLIENT (pas heartbeats !)
  const handleGoToHeartbeats = () => {
    handleDismiss();
    // TOUJOURS rediriger vers l'espace client malin !
    navigate('/client');
  };
  
  if (isDismissed || !isVisible || !currentMessage) return null;
  
  const positionClasses = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'bottom-center': 'bottom-6 left-1/2 -translate-x-1/2',
  };
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.8 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className={`fixed ${positionClasses[position]} z-50 max-w-sm`}
      >
        {/* ✨ Particules magiques */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-pink-400"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 1, 0.3],
                scale: [0.5, 1.2, 0.5],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        
        {/* 💜 Carte principale */}
        <div
          className="relative rounded-3xl overflow-hidden shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))',
            boxShadow: '0 25px 80px rgba(236, 72, 153, 0.4), 0 0 60px rgba(168, 85, 247, 0.3)',
          }}
        >
          {/* Header avec GRAND avatar Pixie */}
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-fuchsia-500 p-4">
            {/* GRAND VISAGE de Pixie au centre */}
            <div className="flex flex-col items-center gap-3">
              <motion.div
                className="relative"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                {/* Glow effect */}
                <div 
                  className="absolute inset-0 rounded-full blur-2xl opacity-50"
                  style={{ background: 'rgba(236, 72, 153, 0.6)', transform: 'scale(1.3)' }}
                />
                <div className="relative w-24 h-24 rounded-full overflow-hidden border-3 border-white/30 shadow-2xl">
                  <img
                    src={PIXIE_IMAGE}
                    alt="Pixie"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full bg-gradient-to-br from-pink-400 to-purple-500 items-center justify-center text-3xl">
                    🧚‍♀️
                  </div>
                </div>
                <motion.div
                  className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center"
                  animate={{ scale: [1, 1.2, 1], rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <Sparkles className="w-4 h-4 text-yellow-800" />
                </motion.div>
              </motion.div>
              
              <div className="text-center">
                <h4 className="font-bold text-white text-xl flex items-center justify-center gap-2">
                  Commandante Pixie
                  <Heart className="w-5 h-5 text-pink-200 fill-pink-200" />
                </h4>
                <p className="text-white/80 text-sm">🚀 Aux commandes de ta soirée ✨</p>
              </div>
            </div>
            
            {/* Bouton fermer */}
            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/20 text-white/80 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Message */}
          <div className="p-5">
            <p className="text-gray-200 text-base leading-relaxed">
              {currentMessage.message}
            </p>
          </div>
          
          {/* Stats fun - Horaires magasin */}
          <div className="px-5 pb-3 flex gap-4 text-sm">
            <div className="flex items-center gap-1.5 text-pink-400">
              <Clock className="w-4 h-4" />
              <span>Ouvert jusqu'à MINUIT</span>
            </div>
            <div className="flex items-center gap-1.5 text-purple-400">
              <ShoppingCart className="w-4 h-4" />
              <span>7j/7</span>
            </div>
          </div>
          
          {/* CTA */}
          <div className="px-5 pb-5 flex gap-3">
            <button
              onClick={handleDismiss}
              className="flex-1 py-3 rounded-xl font-medium text-sm bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
            >
              Plus tard
            </button>
            <motion.button
              onClick={handleGoToHeartbeats}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-pink-500 to-purple-500 hover:opacity-90 flex items-center justify-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {currentMessage.cta}
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PixieGlobal;
